import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionFuturesProgressStep, useProgress } from '@/context/progress';
import type { InstitutionBranchOperations } from '@/features/institution/branch-operations/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';
import { institutionDetailsBranchOperationsDifferentJurisdictionSchema, institutionDetailsBranchOperationsJurisdictionSchema } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { isJurisdictionPermitted } from '@/utils';
import { InstitutionBranchOperationsFieldsetDifferentJurisdiction, InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend } from './fieldsets/different-jurisdiction';
import { InstitutionBranchOperationsFieldsetJurisdiction, InstitutionBranchOperationsFieldsetJurisdictionLegend } from './fieldsets/jurisdiction';

const FORM_STEPS = {
  DIFFERENT_JURISDICTION: {
    Component: InstitutionBranchOperationsFieldsetDifferentJurisdiction,
    Legend: InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend,
    schema: institutionDetailsBranchOperationsDifferentJurisdictionSchema,
  },
  JURISDICTION: {
    Component: InstitutionBranchOperationsFieldsetJurisdiction,
    Legend: InstitutionBranchOperationsFieldsetJurisdictionLegend,
    schema: institutionDetailsBranchOperationsJurisdictionSchema,
  },
} satisfies Record<string, FormStep<InstitutionBranchOperations>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionBranchOperationsFormCurrentStep = () => {
  const { setCurrentStep } = useProgress();
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionBranchOperations>();
  const Step = STEPS[currentStep];

  useEffect(() => {
    return setCurrentStep(
      InstitutionFuturesProgressStep.INSTITUTION_INFORMATION_FUTURES,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'branchOperations', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionBranchOperationsFormCurrentStep.displayName = 'InstitutionBranchOperationsFormCurrentStep';

const InstitutionBranchOperationsFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionBranchOperationsFormCurrentStepLegend.displayName = 'InstitutionBranchOperationsFormCurrentStepLegend';

const InstitutionBranchOperationsFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate('/institution/onboarding/incorporation', { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionBranchOperationsFormBackButton.displayName = 'InstitutionBranchOperationsFormBackButton';

const InstitutionBranchOperationsFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionBranchOperations>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionBranchOperationsFormFooter.displayName = 'InstitutionBranchOperationsFormFooter';

const InstitutionBranchOperationsForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'branchOperations');
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { isPending, mutateAsync } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow || isLoadingCountries;

  const onSubmit = async (data: InstitutionBranchOperations) => {
    await mutateAsync(
      { branchOperations: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          const { differentJurisdiction, countryOfBranchOperations } = data;

          if (differentJurisdiction === 'NO') {
            navigate('/institution/onboarding/company', { replace: true });
            return;
          }

          if (!isJurisdictionPermitted(countryOfBranchOperations, countries ?? {})) {
            navigate(`/institution/onboarding/branch-operations/forbidden-jurisdiction/${countryOfBranchOperations}`, { replace: true });
          } else {
            navigate('/institution/onboarding/company', { replace: true });
          }
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.branchOperations ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionBranchOperationsFormCurrentStepLegend />
        <InstitutionBranchOperationsFormBackButton />
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionBranchOperationsFormCurrentStep />
          <InstitutionBranchOperationsFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionBranchOperationsForm.displayName = 'InstitutionBranchOperationsForm';

export default InstitutionBranchOperationsForm;
